import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { decodeUrl, encodeUrl } from "../lib/url";

const SAMPLE = "https://toolverse.me/search?q=hello world&page=2";

function tabClass(active: boolean) {
  return `rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
    active
      ? "bg-primary text-primary-foreground"
      : "text-muted-foreground hover:text-foreground"
  }`;
}

export function UrlTool() {
  const uid = useId();
  const [mode, setMode] = useState<"encode" | "decode">("encode");
  const [fullUri, setFullUri] = useState(false);
  const [input, setInput] = useState(SAMPLE);
  const [copied, setCopied] = useState(false);

  const result = useMemo(
    () =>
      mode === "encode" ? encodeUrl(input, fullUri) : decodeUrl(input, fullUri),
    [mode, input, fullUri],
  );

  const copy = async () => {
    if (!result.output) return;
    try {
      await navigator.clipboard.writeText(result.output);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex gap-1 rounded-lg border border-border p-1">
            <button
              type="button"
              aria-pressed={mode === "encode"}
              onClick={() => setMode("encode")}
              className={tabClass(mode === "encode")}
            >
              {m.ue_mode_encode()}
            </button>
            <button
              type="button"
              aria-pressed={mode === "decode"}
              onClick={() => setMode("decode")}
              className={tabClass(mode === "decode")}
            >
              {m.ue_mode_decode()}
            </button>
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={fullUri}
              onChange={(e) => setFullUri(e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            {m.ue_full_uri()}
          </label>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-in`}>{m.ue_input_label()}</Label>
          <textarea
            id={`${uid}-in`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={m.ue_placeholder()}
            spellCheck={false}
            className="min-h-[8rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        </div>
      </div>

      <div className="space-y-3 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight">
            {m.ue_output_label()}
          </h2>
          {result.ok && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={copy}
              className="gap-2"
              disabled={!result.output}
            >
              {copied ? (
                <Check className="h-4 w-4 text-emerald-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              {copied ? m.ue_copied() : m.ue_copy()}
            </Button>
          )}
        </div>
        {result.ok ? (
          <textarea
            readOnly
            data-testid="ue-output"
            aria-label={m.ue_output_label()}
            value={result.output}
            spellCheck={false}
            className="min-h-[8rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        ) : (
          <p
            data-testid="ue-error"
            className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 font-mono text-sm text-rose-300"
          >
            {result.error}
          </p>
        )}
      </div>
    </div>
  );
}
